import { AxiosError } from 'axios';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ReactSortable } from 'react-sortablejs';

import Btn from '../../../../../components/Buttons/Btn';
import Input from '../../../../../components/Input';
import Modal from '../../../../../components/Modal';
import {
  backendErrorNotification,
  frontendNotification,
} from '../../../../../components/Notification';
import ReactSortableItem from '../../../../../components/ReactSortableItem';
import SelectAsyncJSX from '../../../../../components/SelectAsync';
import SingleSelect from '../../../../../components/SingleSelect';
import HttpStatus from '../../../../../enums/httpStatus';
import { DeleteIcon, EditIcon } from '../../../../../icons';
import { audienceAPI, formAPI as mainCompAPI } from '../../../../../services/requests';
import {
  Form,
  FormField,
  FormType,
  NewForm,
  NewFormField,
} from '../../../../../types/apiResponse/form';
import { DateInputEvent } from '../../../../../types/events';
import {
  getOptionByValue,
  getOptionsFromAudiences,
  getOptionsFromFormTypes,
  getSelectOptionByBoolean,
  getSelectOptionsFromFormOpeningLocations,
  SelectOptionSpecValue,
} from '../../../../../utils/getSelectOptions';
import { currentDate, getFrequencyOptions, isDateTodayOrPast } from '../../../../../utils/helpers';
// eslint-disable-next-line max-len
import ModalCreateAudience from '../../../Registrations/Audience/Components/ModalCreate/CreateAudienceModal';
import FormFieldsModal from '../ModalCreateAndEditFormField/FormFieldsModal';
import * as s from './FormModalStyle';
import FormRequiredFieldsModal from './FormRequiredFieldsModal';
import { validateForm } from './FormValidations';
import {
  defaultFormData,
  getRequiredFormFieldsNotInForm,
  handleFormTypeChange,
  isFrequencyInputDisabled,
  isIsRequiredInputDisabled,
  isOpeningLocalInputDisabled,
  openingLocationsToBeHidden,
} from './utils';

type Props = {
  isVisible: boolean;
  closeModal: () => void;
  saveCallback: () => void;
  selectedForm?: Form;
};

const FormModal = ({ closeModal, saveCallback, isVisible, selectedForm }: Props) => {
  const { t } = useTranslation();

  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState<NewForm | Form>(selectedForm ?? defaultFormData);

  const [isFieldsModalVisible, setIsFieldsModalVisible] = useState(false);
  const [selectedField, setSelectedField] = useState<FormField | NewFormField | null>(null);

  const [showModalNewAudience, setShowModalNewAudience] = useState(false);
  const [selectAudienceCache, setSelectAudienceCache] = useState(0);

  const [isRequiredFieldsModalVisible, setIsRequiredFieldsModalVisible] = useState(false);

  const [canUpdate, setCanUpdate] = useState(true);
  useEffect(() => {
    if (selectedForm && isDateTodayOrPast(selectedForm.initialDate)) {
      setCanUpdate(false);
    }
  }, [selectedForm]);

  const createOrUpdateRequestApi = async () => {
    const source = mainCompAPI.axios.CancelToken.source();
    try {
      setIsLoading(true);

      validateForm(formData);

      mainCompAPI.query = '';
      let res: any;
      if ('id' in formData) {
        res = await mainCompAPI.update(formData, formData.id);
      } else {
        res = await mainCompAPI.store(formData);
      }

      if (res.status === HttpStatus.OK) {
        setIsLoading(false);
        frontendNotification({
          message: t('success'),
          type: 'success',
        });

        closeModal();
        saveCallback();
      } else {
        throw res;
      }
    } catch (err) {
      if (!mainCompAPI.axios.isCancel(err)) {
        backendErrorNotification(err as AxiosError<any, any>);
      }
    } finally {
      setIsLoading(false);
    }

    return () => {
      source.cancel('Component got unmounted');
    };
  };

  const handleSaveBtn = () => {
    try {
      validateForm(formData);
    } catch (error: any) {
      if (error && error.message) {
        frontendNotification({ message: error.message, type: 'warning' });
      }
      return null;
    }

    if (new Date(formData.initialDate) <= new Date()) {
      if (!window.confirm(t('msg.InitialDateAlert'))) {
        return null;
      }
    }

    createOrUpdateRequestApi();
  };

  const handleRemoveFields = (fieldDescription: string) => {
    setFormData({
      ...formData,
      fields: formData.fields.filter((f) => f.description !== fieldDescription),
    });
  };

  const handleEditQuestion = (fieldDescription: string) => {
    const fieldFound = formData.fields.find((f) => f.description === fieldDescription);
    if (fieldFound) {
      setSelectedField(fieldFound);
      setIsFieldsModalVisible(true);
    }
  };

  const handleOnChangeSortableArea = (list: NewFormField[]) => {
    setFormData({
      ...formData,
      fields: list,
    });
  };

  const handleOnCloseFieldsModal = () => {
    setIsFieldsModalVisible(false);
    setSelectedField(null);
  };

  const handleAddOrEditField = (field: NewFormField) => {
    const fieldAlreadyExists = formData.fields.some((f) => f.description === field.description);
    if (fieldAlreadyExists) {
      const fieldsUpdated = formData.fields.map((f) =>
        f.description === field.description ? field : f,
      );

      setFormData({ ...formData, fields: fieldsUpdated });
    } else {
      const fieldsUpdated = [...formData.fields, field];
      setFormData({ ...formData, fields: fieldsUpdated });
    }

    handleOnCloseFieldsModal();
  };

  return (
    <Modal
      title={t('register')}
      showModal={isVisible}
      closeModal={closeModal}
      cancelButton={closeModal}
      action={() => handleSaveBtn()}
      isLoading={isLoading}
      bodyStyle={formData.fields.length >= 5 ? { overflowX: 'scroll' } : {}}
      {...(!canUpdate && { onlyCloseButton: true })}
    >
      <s.ContainerModalCreate>
        <div className="formInputsArea">
          <div className="form-row">
            <div className="itemFormWithLabel widthContent">
              <label htmlFor="formType">{t('type')}</label>
              <SingleSelect
                id="formType"
                value={getOptionByValue(formData.type, getOptionsFromFormTypes())}
                options={getOptionsFromFormTypes()}
                onChange={(e: SelectOptionSpecValue<FormType>) =>
                  setFormData(handleFormTypeChange(e, formData))
                }
                placeholder={t('type')}
                disabled={isLoading || !canUpdate}
              />
            </div>
            <div className="itemFormWithLabel">
              <label htmlFor="description">{t('description')}</label>
              <Input
                value={formData.description}
                onChange={(e: any) => setFormData({ ...formData, description: e.target.value })}
                disabled={isLoading || !canUpdate}
                style={{ minWidth: '100px' }}
                placeholder={t('description')}
                required=""
                isRequired
                id="description"
              />
            </div>
            <div className="itemFormWithLabel widthContent">
              <label htmlFor="initialDate">{t('initialDate')}</label>
              <input
                type="date"
                className="input"
                id="initialDate"
                name="initialDate"
                value={formData.initialDate}
                onChange={(e: DateInputEvent) =>
                  setFormData({ ...formData, initialDate: e.target.value })
                }
                min={currentDate()}
                disabled={isLoading || !canUpdate}
              />
            </div>
            <div className="itemFormWithLabel widthContent">
              <label htmlFor="finalDate">{t('finalDate')}</label>
              <input
                type="date"
                className="input"
                id="finalDate"
                name="finalDate"
                value={formData.finalDate}
                onChange={(e: DateInputEvent) =>
                  setFormData({ ...formData, finalDate: e.target.value })
                }
                min={currentDate()}
                disabled={isLoading || !canUpdate}
              />
            </div>
            <div className="itemFormWithLabel widthContent">
              <label htmlFor="">{t('isRequired')}</label>
              <SingleSelect
                id="isRequired"
                value={getSelectOptionByBoolean(formData.isRequired)}
                options={[
                  { value: true, label: t('yes') },
                  { value: false, label: t('not') },
                ]}
                onChange={(e) => setFormData({ ...formData, isRequired: e.value })}
                placeholder={t('yesOrNo')}
                disabled={isIsRequiredInputDisabled(formData) || !canUpdate}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="itemFormWithLabel widthContent">
              <label htmlFor="frequency">{t('frequency')}</label>
              <SingleSelect
                value={getOptionByValue(formData.frequency, getFrequencyOptions())}
                onChange={(e: SelectOptionSpecValue<number>) =>
                  setFormData({ ...formData, frequency: e.value })
                }
                options={getFrequencyOptions()}
                placeholder={t('frequency')}
                isRequired
                id="frequency"
                disabled={isFrequencyInputDisabled(formData) || !canUpdate}
              />
            </div>
            <div className="itemFormWithLabel widthContent">
              <label htmlFor="placeOfExecution">{t('placeOfExecution')}</label>
              <SingleSelect
                value={getOptionByValue(
                  formData.openingLocal,
                  getSelectOptionsFromFormOpeningLocations(),
                )}
                onChange={(e: SelectOptionSpecValue<number>) =>
                  setFormData({ ...formData, openingLocal: e.value })
                }
                // options={getSelectOptionsFromFormOpeningLocations()}
                options={openingLocationsToBeHidden(formData)}
                placeholder={t('placeOfExecution')}
                id="placeOfExecution"
                disabled={isOpeningLocalInputDisabled(formData) || !canUpdate}
              />
            </div>
            <div className="itemFormWithLabel widthContent">
              <label htmlFor="audience">{t('audience')}</label>
              <SelectAsyncJSX
                value={
                  formData.audience
                    ? { value: formData.audience.id, label: formData.audience.description }
                    : null
                }
                onChange={(e: SelectOptionSpecValue<number>) => {
                  if (!e) {
                    return setFormData({ ...formData, audienceId: null, audience: null });
                  } else {
                    return setFormData({
                      ...formData,
                      audienceId: e.value,
                      audience: { id: e.value, description: e.label },
                    });
                  }
                }}
                request={audienceAPI}
                reqResponseToOption={getOptionsFromAudiences}
                placeholder={t('audience')}
                isRequired
                cacheUniqs={[selectAudienceCache]}
                id="audience"
                disabled={isLoading || !canUpdate}
              />
            </div>
            <div className="itemFormWithoutLabel">
              <Btn
                text={t('newAudience')}
                disabled={isLoading || !canUpdate}
                onClick={() => setShowModalNewAudience(true)}
              />
            </div>
          </div>
        </div>

        {getRequiredFormFieldsNotInForm(formData).length > 0 && isRequiredFieldsModalVisible && (
          <FormRequiredFieldsModal
            requiredFields={getRequiredFormFieldsNotInForm(formData)}
            onClose={() => setIsRequiredFieldsModalVisible(false)}
          />
        )}

        <div className="form-row">
          <div className="itemFormWithoutLabel" style={{ maxWidth: 'max-content' }}>
            <Btn
              text={t('addField')}
              onClick={() => setIsFieldsModalVisible(true)}
              disabled={isLoading || !canUpdate}
            />
          </div>
          {getRequiredFormFieldsNotInForm(formData).length > 0 && (
            <div className="itemFormWithoutLabel">
              <Btn
                type="text"
                text={t('showRequiredFields') as string}
                onClick={() => setIsRequiredFieldsModalVisible(true)}
              />
            </div>
          )}
        </div>

        <div className="formFieldsArea">
          {formData.fields.length <= 0 && <s.NoQuestion>{t('noField')}</s.NoQuestion>}

          <ReactSortable
            list={formData.fields.map((field, index) => {
              return { ...field, id: index };
            })}
            setList={handleOnChangeSortableArea}
            style={{ margin: '0.7rem' }}
          >
            {formData.fields.map((item) => (
              <ReactSortableItem key={item.description}>
                <div className="field-sortable-item">
                  <label>{item.description}</label>

                  <div className="field-sortable-item-icon">
                    <EditIcon
                      title={t('edit')}
                      onClick={() => handleEditQuestion(item.description)}
                      size="1.5rem"
                      disabled={isLoading || !canUpdate}
                    />

                    <DeleteIcon
                      title={t('delete')}
                      onClick={() => handleRemoveFields(item.description)}
                      size="1.5rem"
                      disabled={isLoading || !canUpdate}
                    />
                  </div>
                </div>
              </ReactSortableItem>
            ))}
          </ReactSortable>
        </div>

        {isFieldsModalVisible && (
          <FormFieldsModal
            isVisible={isFieldsModalVisible}
            closeModal={handleOnCloseFieldsModal}
            saveActionBtn={handleAddOrEditField}
            selectedField={selectedField}
          />
        )}

        {showModalNewAudience && (
          <ModalCreateAudience
            closeModal={() => setShowModalNewAudience(false)}
            callback={() => setSelectAudienceCache(selectAudienceCache + 1)}
            availableTypes={['seller']}
          />
        )}
      </s.ContainerModalCreate>
    </Modal>
  );
};

export default FormModal;
